import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, AddOnCategory } from '../../services/dataService';

interface AddOnCategoryModalProps {
  category?: AddOnCategory | null;
  parentCategory?: AddOnCategory | null; // For creating sub-categories
  onClose: () => void;
  onSuccess: () => void;
}

const AddOnCategoryModal: React.FC<AddOnCategoryModalProps> = ({ 
  category, 
  parentCategory,
  onClose, 
  onSuccess 
}) => {
  const { tenantData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isRequired: false,
    minSelections: 0,
    maxSelections: 0,
    displayOrder: 0,
    parentCategoryId: '',
    isSubCategory: false,
    level: 0
  });

  useEffect(() => {
    console.log('useEffect triggered with:', { category, parentCategory });
    
    if (category) {
      console.log('Setting up edit form for category:', category);
      setFormData({
        name: category.name,
        description: category.description || '',
        isRequired: category.isRequired,
        minSelections: category.minSelections,
        maxSelections: category.maxSelections,
        displayOrder: category.displayOrder,
        parentCategoryId: category.parentCategoryId || '',
        isSubCategory: category.isSubCategory || false,
        level: category.level || 0
      });
    } else if (parentCategory) {
      // Creating a sub-category
      console.log('Setting up sub-category form with parent:', parentCategory);
      setFormData(prev => ({
        ...prev,
        parentCategoryId: parentCategory.id!,
        isSubCategory: true,
        level: (parentCategory.level || 0) + 1,
        displayOrder: 0
      }));
    } else {
      // Creating a top-level category
      console.log('Setting up top-level category form');
      setFormData(prev => ({
        ...prev,
        displayOrder: 0,
        isSubCategory: false,
        level: 0
      }));
    }
  }, [category, parentCategory]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    console.log('Input change:', { name, value, type });
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    console.log('Checkbox change:', { name, checked });
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseInt(value) || 0;
    console.log('Number input change:', { name, value, numValue });
    setFormData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenantData?.id) return;
    
    // Validation
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }
    
    if (formData.minSelections < 0) {
      setError('Minimum selections cannot be negative');
      return;
    }
    
    if (formData.maxSelections < 0) {
      setError('Maximum selections cannot be negative');
      return;
    }
    
    if (formData.maxSelections > 0 && formData.minSelections > formData.maxSelections) {
      setError('Minimum selections cannot be greater than maximum selections');
      return;
    }
    
    if (formData.isRequired && formData.minSelections === 0) {
      setError('Required categories must have at least 1 minimum selection');
      return;
    }

    try {
      setLoading(true);
      setError('');

      console.log('Form data being submitted:', formData);
      console.log('Tenant ID:', tenantData.id);

      if (category) {
        // Update existing category
        console.log('Updating existing category:', category.id);
        await dataService.updateAddOnCategory(tenantData.id, category.id!, formData);
      } else {
        // Create new category
        console.log('Creating new category');
        await dataService.saveAddOnCategory(tenantData.id, formData);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving add-on category:', error);
      console.error('Error details:', {
        code: (error as any).code,
        message: (error as any).message,
        stack: (error as any).stack
      });
      setError('Failed to save category: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {category ? 'Edit Add-On Category' : 'Add Add-On Category'}
            </h2>
            {parentCategory && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Creating sub-category under "{parentCategory.name}"
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Category Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="e.g., Toppings, Cheese, Size"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Optional description for this category"
            />
          </div>

          {/* Required Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isRequired"
              name="isRequired"
              checked={formData.isRequired}
              onChange={handleCheckboxChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isRequired" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Customer must select at least one option from this category
            </label>
          </div>

          {/* Min Selections */}
          <div>
            <label htmlFor="minSelections" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Minimum Selections
            </label>
            <input
              type="number"
              id="minSelections"
              name="minSelections"
              value={formData.minSelections}
              onChange={handleNumberChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Minimum number of options customer must select
            </p>
          </div>

          {/* Max Selections */}
          <div>
            <label htmlFor="maxSelections" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Maximum Selections
            </label>
            <input
              type="number"
              id="maxSelections"
              name="maxSelections"
              value={formData.maxSelections}
              onChange={handleNumberChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Maximum number of options customer can select (0 = unlimited)
            </p>
          </div>

          {/* Display Order */}
          <div>
            <label htmlFor="displayOrder" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Display Order
            </label>
            <input
              type="number"
              id="displayOrder"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleNumberChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Order in which this category appears (lower numbers appear first)
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <span>{category ? 'Update' : 'Create'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
};

export default AddOnCategoryModal;

